import * as React from "react";
import {
  Button,
  ButtonProps,
  IconButton,
  Tooltip,
  TooltipProps,
} from "@mui/material";

export interface ActionButtonProps {
  // Button props
  ariaLabel?: string;
  children?: React.ReactNode; //Button text / content (ignored when iconOnly is true)
  color?: ButtonProps["color"];
  disabled?: ButtonProps["disabled"];
  fullWidth?: ButtonProps["fullWidth"];
  size?: "small" | "medium" | "large";
  variant?: ButtonProps["variant"];
  onClick: React.MouseEventHandler<HTMLButtonElement>; //Click handler supplied by caller

  // Icon props
  icon?: React.ElementType; // Pass the icon component type (memo-safe)
  iconOnly?: boolean; //When true, renders IconButton instead of Button

  // Tooltip Props
  tooltipText?: string;
  tooltipArrow?: TooltipProps["arrow"];
  tooltipPlacement?: TooltipProps["placement"];
}

export const ActionButton = React.memo(function ActionButton(
  props: ActionButtonProps
) {
  const {
    // Button Props
    ariaLabel,
    children,
    color = "primary",
    size = "medium",
    variant = "contained",
    disabled = false,
    fullWidth = false,
    onClick,

    // Icon props
    icon: Icon, // rename so we can render <Icon />
    iconOnly = false,

    // Tooltip Props
    tooltipText = "",
    tooltipArrow = true,
    tooltipPlacement = "top",
  } = props;

  const inferredAriaLabel =
    ariaLabel ??
    (tooltipText ? tooltipText : undefined) ??
    (typeof children === "string" ? children : undefined) ??
    "action button";

  const iconNode = Icon ? <Icon /> : undefined;

  const button = iconOnly ? (
    <IconButton
      aria-label={inferredAriaLabel}
      onClick={onClick}
      disabled={disabled}
      color={color}
      size={size}
    >
      {iconNode}
    </IconButton>
  ) : (
    <Button
      aria-label={inferredAriaLabel}
      variant={variant}
      size={size}
      color={color}
      startIcon={iconNode}
      onClick={onClick}
      disabled={disabled}
      fullWidth={fullWidth}
      sx={{ width: "100%" }}
    >
      {children}
    </Button>
  );

  // Disabled buttons do not fire mouse events, so Tooltip must wrap a
  // hoverable element. alignItems:center keeps the button at its natural
  // height if a row flex parent stretches this wrapper.
  return (
    <Tooltip
      disableHoverListener={!tooltipText}
      title={tooltipText}
      arrow={tooltipArrow}
      placement={tooltipPlacement}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          maxWidth: "100%",
          width: !iconOnly && fullWidth ? "100%" : undefined,
          ...(iconOnly || fullWidth ? {} : { alignSelf: "stretch" }),
        }}
      >
        {button}
      </span>
    </Tooltip>
  );
});
